import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Scale, Shield, Clock, Award, Users, ChevronRight, Anchor, Car, BadgeCheck, Heart, DollarSign, Calculator, AlertCircle, Mail } from 'lucide-react';
import GoogleReviews from '@/components/GoogleReviews';
import CompensationEstimator from '@/components/CompensationEstimator';
import SEOHead from '@/components/SEOHead';

const HomePage = () => {
  const practiceAreas = [
    {
      title: 'Car Crashes',
      description: 'Insurance companies calling, medical providers billing, nothing but questions. I\'ve been there myself. I\'ve helped countless clients in your position.',
      icon: Car,
      link: '/car-crashes',
      image: 'https://customer-assets.emergentagent.com/job_502b6e34-4bf8-4ac1-b1fb-665aceb7b70a/artifacts/bzpscp8i_car-crash-from-car-accident-on-the-road-in-wait-insurance.jpeg',
      imageAlt: 'Car accident scene - Washington State auto collision injury attorney'
    },
    {
      title: 'Maritime Injuries',
      description: 'If you\'ve been hurt while working aboard a vessel, call me. I have extensive experience in dealing with maritime injuries.',
      icon: Anchor,
      link: '/maritime-injuries',
      image: 'https://customer-assets.emergentagent.com/job_502b6e34-4bf8-4ac1-b1fb-665aceb7b70a/artifacts/07ltua4e_harbour-618198_640.jpg',
      imageAlt: 'Commercial harbor with vessels - maritime injury lawyer for Washington and Alaska workers'
    },
    {
      title: 'Seaman Injuries',
      description: 'The Jones Act protects those working at sea. I have worked on ships myself and I have worked with large maritime corporations and know strategy.',
      icon: Anchor,
      link: '/maritime-injuries',
      image: 'https://customer-assets.emergentagent.com/job_502b6e34-4bf8-4ac1-b1fb-665aceb7b70a/artifacts/dt9v7ull_trawler-off-into-an-inlet.jpeg',
      imageAlt: 'Fishing trawler vessel - Jones Act attorney for injured seamen and deckhands'
    },
    {
      title: 'First Responder Crashes',
      description: 'Peace officers often assume they cannot sue criminals for injuries. This is not accurate. I secured a six-figure settlement for an on-duty officer.',
      icon: Shield,
      link: '/first-responder-crashes',
      image: 'https://images.unsplash.com/photo-1759933633339-2382db138c2f?w=800&auto=format',
      imageAlt: 'Emergency vehicle - first responder injury attorney for police officers and peace officers in Washington'
    },
    {
      title: 'At-Work Crashes',
      description: 'I\'ve secured money for clients, like one hit by a reckless driver on the job, when my client thought he was bound by worker\'s comp or what his employer told him.',
      icon: Car,
      link: '/car-crashes',
      image: 'https://images.unsplash.com/photo-1653924323287-127daf6e6937?w=800&auto=format',
      imageAlt: 'Work vehicle accident - on-the-job car crash lawyer beyond workers compensation claims'
    },
    {
      title: 'Slip and Falls',
      description: 'It\'s important that after a slip and fall, you protect your rights and prove liability. I can help secure fair compensation for medical costs and lost wages.',
      icon: BadgeCheck,
      link: '/slip-and-falls',
      image: 'https://images.unsplash.com/photo-1717298812095-692414f0cb13?w=800&auto=format',
      imageAlt: 'Premises liability - slip and fall accident attorney for injuries at stores and commercial properties'
    },
    {
      title: 'Motorcycle Crashes',
      description: 'Motorcycle accidents can result in severe injuries. I fight to ensure you receive maximum compensation for your recovery.',
      icon: Shield,
      link: '/motorcycle-crashes',
      image: 'https://images.unsplash.com/photo-1675868934032-0607db7734dd?w=800&auto=format',
      imageAlt: 'Motorcycle on road - motorcycle accident injury lawyer serving Washington State riders'
    },
    {
      title: 'Wrongful Death',
      description: 'I have been personally involved with a wrongful death. I feel what you\'re going through. I handle legal complexities, so you can begin to heal.',
      icon: Heart,
      link: '/wrongful-death',
      image: 'https://images.unsplash.com/photo-1637563954989-0d522aa0f7f6?w=800&auto=format',
      imageAlt: 'Peaceful waters - wrongful death attorney providing compassionate legal support in Washington'
    }
  ];

  const caseExamples = [
    {
      title: 'Retained Surgical-Tool Fragment',
      description: 'I secured a confidential settlement for a client after a broken surgical-tool fragment was left inside the body during a surgical procedure, requiring a second surgery.'
    },
    {
      title: 'Wet-Floor Slip and Fall',
      description: 'I obtained a six-figure settlement for a client whose dominant wrist was fractured after slipping on a wet floor at a business — the injury required surgery and permanent hardware.'
    },
    {
      title: 'On-Duty First Responder Injuries',
      description: 'I obtained a six-figure settlement for an on-duty peace officer who sustained a surgical injury after lawful intervention in a pursuit.'
    },
    {
      title: 'Workers Injured While Driving',
      description: 'I successfully settled a claim for a worker injured on the job by a drunk driver, in which there were significant gaps in medical treatment.'
    },
    {
      title: 'Injuries with Preexisting Conditions',
      description: 'I won arbitration of a rear end collision against a major auto insurer with significant preexisting injury.'
    },
    {
      title: 'Maximize Settlement Offers',
      description: 'Many cases settled for policy limits for minor non-surgical injuries.'
    }
  ];

  // FAQ data for homepage schema - common questions visitors have
  const homepageFAQs = [
    {
      question: "How much does it cost to hire a personal injury attorney?",
      answer: "I work on a contingency fee basis, meaning you pay nothing upfront. My fee is a percentage of your settlement or award, and you only pay if I win your case. This allows you to focus on recovery without financial stress."
    },
    {
      question: "Why should I choose a solo attorney over a big law firm?",
      answer: "With my firm, you work directly with me—not a paralegal or associate. I personally handle your case from start to finish, providing one-on-one attention, faster communication, and a compassionate approach that large firms often can't offer."
    },
    {
      question: "What types of personal injury cases do you handle?",
      answer: "I handle car accidents, maritime injuries (Jones Act claims), motorcycle crashes, first responder injuries, slip and falls, and wrongful death cases throughout Washington State and Alaska."
    },
    {
      question: "How long do I have to file a personal injury claim in Washington?",
      answer: "Washington has a 3-year statute of limitations for most personal injury claims (RCW 4.16.080). However, I recommend contacting an attorney immediately—evidence disappears quickly and early action strengthens your case."
    },
    {
      question: "What should I do immediately after an accident?",
      answer: "Seek medical attention, document the scene with photos, gather witness information, file a police report, and avoid giving recorded statements to insurance companies before speaking with an attorney."
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOHead 
        title="Amy C. Brown Law | Personal Injury Attorney | Everett, WA"
        description="Experienced personal injury attorney in Everett, Washington serving WA State and Alaska. Maritime injuries, car crashes, first responder cases. Free consultation. No fees unless you win."
        faqData={homepageFAQs}
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-44 pb-16 overflow-hidden" data-testid="hero-section">
        {/* Background Video (Desktop) / Image (Mobile) */}
        <div className="absolute inset-0 z-0">
          {/* Light grey background - subtle professional look */}
          <div className="absolute inset-0 bg-slate-50"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight hero-animate text-secondary">
                A Personal Injury Lawyer Who Understands <span className="text-[#4A9B9B]">How Insurance Companies Think</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed hero-animate-delay-1">
                Helping injured clients across Washington navigate insurance claims and move forward after serious accidents.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10 hero-animate-delay-2">
                <a 
                  href="tel:425-675-1224"
                  data-testid="hero-call-btn"
                  className="inline-flex items-center justify-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary/90 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  data-testid="hero-text-btn"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me
                </a>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 text-center hero-animate-delay-3">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-sm text-secondary font-medium">10+ Years<br/>Experience</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <Scale className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-sm text-secondary font-medium">No Fees<br/>Unless You Win</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-sm text-secondary font-medium">Work Directly<br/>With Me</p>
                </div>
              </div>

              {/* Award Badges */}
              <div className="flex items-center justify-center gap-6 mt-8 hero-animate-delay-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/aecnwanu_10Best.png"
                  alt="10 Best Personal Injury Attorney 2025 Award - American Institute of Personal Injury Attorneys Client Satisfaction"
                  title="Amy C. Brown - 10 Best Attorney Award 2025"
                  className="h-20 md:h-24 w-auto"
                  data-testid="award-10best"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/2s3q26fe_top10.webp"
                  alt="Top 10 Personal Injury Attorney 2025 Award - Attorney and Practice Magazine Washington State"
                  title="Top 10 Personal Injury Lawyer Washington 2025"
                  className="h-20 md:h-24 w-auto"
                  data-testid="award-top10"
                />
              </div>
            </div>

            {/* Amy's Photo with Experience Badge */}
            <div className="mt-8 lg:mt-0">
              <div className="relative max-w-xs mx-auto lg:max-w-md">
                <div className="absolute -inset-4 bg-teal-500/20 rounded-3xl blur-2xl"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_site-from-prompt-2/artifacts/0lt38mib_profile-1.webp"
                  alt="Amy C. Brown - Everett Washington personal injury attorney with 10+ years experience in car accidents, maritime injuries, and Jones Act claims"
                  title="Personal injury lawyer Amy C. Brown - Everett WA"
                  className="relative rounded-2xl shadow-2xl w-full"
                  data-testid="hero-amy-photo"
                />
                {/* 10+ Years Experience Badge */}
                <div className="absolute -bottom-3 -left-3 lg:-bottom-4 lg:-left-4 bg-primary text-white px-4 py-3 lg:px-6 lg:py-4 rounded-xl shadow-xl">
                  <p className="font-heading text-2xl lg:text-3xl font-bold">10<sup>+</sup></p>
                  <p className="text-xs lg:text-sm font-medium">Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Study Stat */}
      <section className="py-12 bg-accent-soft" data-testid="stats-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
            <div className="bg-teal-600 text-white px-8 py-4 rounded-2xl font-heading text-4xl font-bold">
              3.5×
            </div>
            <p className="text-lg text-slate-700 max-w-2xl">
              Hiring an experienced personal injury attorney can significantly increase your settlement, as studies from the Insurance Research Council show that represented claimants receive <strong>3.5 times more on average</strong>—even after attorney fees—compared to those handling claims alone.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Video - Near Top */}
      <section className="py-16 bg-white" data-testid="featured-video-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
              Meet Amy
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              See Why Clients Trust Me With Their Cases
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Watch this short video to learn about my approach to personal injury law and how I fight for maximum compensation.
            </p>
          </div>
          
          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
            <iframe
              src="https://www.youtube.com/embed/XthMPOpVWNo?rel=0&modestbranding=1"
              title="Personal Injury Attorney - Amy C. Brown"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          
          <div className="mt-6 text-center">
            <a 
              href="https://www.youtube.com/@amycbrownlaw" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
              Watch more videos on YouTube
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-white" data-testid="pain-points-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                An accident can leave you overwhelmed.
              </h2>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 text-xl mt-0.5">√</span>
                  <span className="text-lg text-slate-600">You may be in pain.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 text-xl mt-0.5">√</span>
                  <span className="text-lg text-slate-600">Insurance companies pressuring you to sign.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 text-xl mt-0.5">√</span>
                  <span className="text-lg text-slate-600">Hospital bills are piling up fast.</span>
                </li>
              </ul>

              <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-teal-500">
                <p className="text-slate-700 leading-relaxed mb-4">
                  At Amy C Brown Law, I'm more than an attorney—<strong>I've experienced trauma myself.</strong> Multiple members of my family were killed in a construction-related incident.
                </p>
                <p className="text-slate-700 leading-relaxed mb-4">
                  I vividly remember that horrible day and the days after. I know pain and I understand the pain my clients feel.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Maybe you've never dealt with lawyers before, and trusting one feels daunting. I've been a lawyer for over ten years and have guided countless people through the chaos you may be going through. <strong>I'll listen, explain your options clearly, and handle the complexities so you can heal.</strong>
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 hover-lift">
                <h3 className="font-heading text-xl font-semibold text-secondary mb-4">Clear Communication</h3>
                <p className="text-slate-600 leading-relaxed">
                  One of the most common frustrations clients express about lawyers is the lack of regular updates, leaving them in the dark about their cases. I share this concern and am deeply committed to ensuring that every client I represent is kept fully informed every step of the way, providing clear and timely communication throughout your case.
                </p>
              </div>

              <div className="bg-teal-600 rounded-2xl p-8 text-white">
                <p className="text-lg font-medium mb-4">
                  Call me for a free, no-pressure consultation:
                </p>
                <a 
                  href="tel:425-675-1224"
                  className="text-3xl font-heading font-bold hover:text-teal-200 transition-colors"
                  data-testid="pain-section-phone"
                >
                  425-675-1224
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens Next - Process Roadmap */}
      <section className="py-20 bg-white" data-testid="what-happens-next-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              What Happens Next
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Many people feel overwhelmed after an accident. The legal process may feel unfamiliar, but I guide clients through each step so they understand what is happening and what comes next.
            </p>
          </div>

          {/* 5-Step Process */}
          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden lg:block absolute top-24 left-[10%] right-[10%] h-1 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 rounded-full" />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
              {/* Step 1 */}
              <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 bg-[#4A9B9B] rounded-2xl flex items-center justify-center shadow-lg">
                      <MessageCircle className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-sm shadow">
                      1
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Initial Conversation</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    We begin with a conversation about what happened, the injuries involved, and what has occurred with insurance so far. I listen carefully and help you understand your options.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 bg-[#4A9B9B] rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-sm shadow">
                      2
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Taking Over Communication</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Once representation begins, I communicate with insurance companies and other parties. This allows you to focus on recovery instead of handling calls and paperwork.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 bg-[#4A9B9B] rounded-2xl flex items-center justify-center shadow-lg">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-sm shadow">
                      3
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Medical Care & Documentation</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Your health and recovery come first. While you focus on treatment, I gather records, bills, and documentation needed to understand your case.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 bg-[#4A9B9B] rounded-2xl flex items-center justify-center shadow-lg">
                      <Scale className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-sm shadow">
                      4
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Evaluating the Claim</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Your case is reviewed carefully to understand liability, injuries, and insurance coverage. This helps determine how the claim should move forward.
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 bg-[#4A9B9B] rounded-2xl flex items-center justify-center shadow-lg">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-sm shadow">
                      5
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Negotiation & Resolution</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Many cases resolve through negotiation once facts and medical information are clear. I prepare each case thoughtfully and discuss options so you can make informed decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Reassurance Paragraph */}
          <div className="mt-16 max-w-3xl mx-auto text-center">
            <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-primary">
              <p className="text-slate-700 leading-relaxed">
                Every case is different, but you can expect clear communication, thoughtful guidance, and direct access to me throughout the process. My goal is to help you navigate this situation with clarity and confidence.
              </p>
            </div>
          </div>

          {/* Questions Clients Often Ask */}
          <div className="mt-16">
            <h3 className="font-heading text-2xl font-bold text-secondary text-center mb-10">
              Questions Clients Often Ask
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="font-heading font-semibold text-secondary">How much does it cost to get started?</h4>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Personal injury cases are handled on a contingency fee basis, meaning attorney fees are only paid if there is a recovery. The fee structure is explained clearly during our initial conversation.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="font-heading font-semibold text-secondary">Will I be working directly with a lawyer?</h4>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Yes. You work directly with me, Amy Brown, and receive clear communication throughout your case. I personally handle your matter from start to finish.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="font-heading font-semibold text-secondary">How long will the case take?</h4>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Timelines vary depending on injuries, treatment, and complexity. You receive guidance and updates as your case progresses, so you always know where things stand.
                </p>
              </div>
            </div>
          </div>

          {/* Soft CTA */}
          <div className="mt-16 text-center">
            <p className="text-slate-600 mb-6">
              If you have questions about what happens next or would like to discuss your situation, you are welcome to contact me for a conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-colors"
                data-testid="what-happens-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-colors"
                data-testid="what-happens-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Grid - Moved up for better UX */}
      <section className="py-20 bg-slate-50" data-testid="practice-areas-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Practice Areas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              I handle injury cases from Bellingham to Vancouver, Bainbridge Island to Spokane, and all cities in between.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {practiceAreas.map((area, index) => (
              <Link 
                key={index}
                to={area.link}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover-lift border border-slate-100"
                data-testid={`practice-area-${area.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={area.image}
                    alt={area.imageAlt || area.title}
                    title={area.title + ' - Washington State injury attorney'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent"></div>
                  <div className="absolute bottom-3 left-4 right-4">
                    <h3 className="font-heading text-lg font-semibold text-white">
                      {area.title}
                    </h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-slate-600 text-sm mb-3 line-clamp-3">
                    {area.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                    Learn more <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Compensation Estimator Section */}
      <section className="py-20 bg-accent-soft" data-testid="estimator-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Free Educational Tool
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Wondering What Your Case Might Be Worth?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Use my free Potential Compensation Estimator to get a rough idea of your case value. Answer six simple questions — no contact info required.
              </p>
              <ul className="space-y-3 text-slate-600 mb-8">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  100% free, no strings attached
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  Educational estimate in under 2 minutes
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  No personal information required
                </li>
              </ul>
              
              {/* Important Disclaimer */}
              <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-primary mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-secondary mb-2">Important Disclaimer</h4>
                    <p className="text-slate-600 text-sm leading-relaxed mb-3">
                      This is an educational tool only — every case is unique. The estimates provided are not guarantees and should not be considered legal advice.
                    </p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <a 
                        href="tel:425-675-1224"
                        className="inline-flex items-center gap-1.5 text-primary font-medium hover:text-coral-600"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        (425) 675-1224
                      </a>
                      <a 
                        href="mailto:amy@amycbrownlaw.com"
                        className="inline-flex items-center gap-1.5 text-primary font-medium hover:text-coral-600"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        amy@amycbrownlaw.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <Link 
                to="/compensation-estimator"
                className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700 transition-colors"
                data-testid="estimator-full-page-link"
              >
                View full estimator page with more details
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            <div>
              <CompensationEstimator />
            </div>
          </div>
        </div>
      </section>

      {/* Case Examples */}
      <section className="py-20 bg-white" data-testid="case-examples-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Some of My Case Examples
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {caseExamples.map((example, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover-lift"
                data-testid={`case-example-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-secondary mb-2">
                      {example.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {example.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg"
              data-testid="case-examples-cta"
            >
              <Phone className="w-5 h-5" />
              Call or Text Me Today: 425-675-1224
            </a>
          </div>
        </div>
      </section>

      {/* Contingency Fee Section */}
      <section className="py-20 bg-secondary text-white" data-testid="contingency-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                No Fees Unless You Win
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                How Much Does Hiring a Lawyer Cost?
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">
                I operate on a contingency fee basis, meaning you do not pay upfront legal fees. My compensation is contingent upon securing a successful outcome for your case—only when you win do I receive a percentage of the settlement or award.
              </p>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                This approach alleviates financial pressure during an already challenging time, allowing you to focus on recovery rather than costs. With over ten years of experience, I am committed to maximizing your recovery, ensuring my interests align with yours.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/pricing"
                  data-testid="contingency-pricing-link"
                  className="inline-flex items-center gap-2 bg-teal-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-600 transition-all duration-300"
                >
                  <DollarSign className="w-5 h-5" />
                  Full Pricing Details
                </Link>
                <a 
                  href="tel:425-675-1224"
                  data-testid="contingency-call-btn"
                  className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
                >
                  <Phone className="w-5 h-5" />
                  Free Consultation
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Scale className="w-10 h-10 text-teal-400 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">No Upfront Costs</h4>
                <p className="text-sm text-slate-400">Focus on healing, not fees</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-teal-400 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Risk-Free</h4>
                <p className="text-sm text-slate-400">I only get paid if you win</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Award className="w-10 h-10 text-teal-400 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">10+ Years</h4>
                <p className="text-sm text-slate-400">Proven experience</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Heart className="w-10 h-10 text-teal-400 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Woman Owned</h4>
                <p className="text-sm text-slate-400">Personal commitment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* Experience Section */}
      <section className="py-20 bg-white" data-testid="experience-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Over Ten Years of Experience You Can Trust
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                After graduating with honors from Seattle University School of Law, where I served as an intern for a federal judge in the Western District of Washington, I worked at a law firm representing prominent national clients. This experience provided me with valuable insight into insurance company strategies, enabling me to build compelling cases for my clients to maximize their settlement outcomes.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Expertise in insurance tactics</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Skilled in developing strategic approaches</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">No fees unless you win</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Woman owned</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">I sincerely want to help people</span>
                </li>
              </ul>
            </div>

            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/9i75gaa1_IMG_2997-scaled.jpg"
                alt="Amy C. Brown - Washington personal injury attorney with over 10 years experience helping car accident and maritime injury victims"
                title="Experienced personal injury lawyer Everett WA"
                loading="lazy"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white rounded-2xl p-6 shadow-xl">
                <p className="text-3xl font-heading font-bold">10+</p>
                <p className="text-teal-200">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Amy Preview - Moved lower for better engagement */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="about-preview-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Learn About Amy C Brown
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Personal Injury Attorney for Cases in Washington State and Alaska
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Unique Perspective from Both Sides
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Having started my career defending employers, shipowners, and insurance companies, I gained a rare understanding of their strategies, which I now use to effectively represent plaintiffs, ensuring fair treatment for those injured at sea or on land.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Personalized Support as a Solo Practicing Attorney
              </h3>
              <p className="text-slate-600 leading-relaxed">
                As a solo practitioner, I offer direct, one-on-one communication, answering every call and email myself to keep clients informed and supported, eliminating the frustration of navigating large firm hierarchies.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Anchor className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                From Sea to Courtroom
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Growing up in the Puget Sound and working on vessels in Southeast Alaska and Mexico's Sea of Cortez, I developed a deep connection to the maritime world, which I now channel into my legal practice to advocate for seamen and people involved in car crashes and other types of injury.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/about"
              data-testid="learn-more-about-link"
              className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700 transition-colors"
            >
              Learn more about my background
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Solo Attorney */}
      <section className="py-20 bg-white" data-testid="why-solo-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Why Choose a Solo Attorney?
              </h2>
              <h3 className="font-heading text-xl text-teal-600 mb-4">
                Benefits of Choosing a Small Firm
              </h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  <span className="text-slate-700 font-medium">Work Directly with Me</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  <span className="text-slate-700 font-medium">I Work Your Entire Case</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  <span className="text-slate-700 font-medium">Compassionate and Strategic</span>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed">
                The distinct advantage of choosing a small firm like mine lies in the personal attention you will experience. Large firms frequently delegate the majority of legal work to paralegals, and when you call, it is often a paralegal or assistant who responds, with the attorney remaining less involved.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                With my firm, you work directly with me. I am personally committed to handling your case, negotiating with insurance companies, and collaborating with medical providers on your behalf. The stress of navigating a large firm can be as overwhelming as the injury itself, and I recognize the emotional burden you are already enduring. What you need is a calm, compassionate, strategic approach—that's how I handle cases.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Why My Defense Experience Matters
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  <span className="text-slate-700 font-medium">Strategic Defense Insight</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  <span className="text-slate-700 font-medium">Skilled Evidence Handling</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </span>
                  <span className="text-slate-700 font-medium">Maximized Case Outcomes</span>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed">
                Choosing a personal injury attorney with defense experience can significantly influence your case's success. My background at a defense firm provides a unique perspective, equipping me with strategic defense insight into how opposing counsel operates, allowing me to anticipate and counter their tactics effectively.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                I am skilled in evidence handling, adept at challenging claims and identifying weaknesses in the opposition's case. This expertise enables me to craft robust strategies, ensuring maximized case outcomes and enhancing your chances of a favorable resolution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timely Action CTA */}
      <section className="py-20 bg-teal-600 text-white" data-testid="timely-action-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Take Timely Action to Protect Your Case
          </h2>
          <p className="text-xl text-teal-100 leading-relaxed mb-8">
            Do not let precious time slip away—acting swiftly is critical to protect your case. Evidence can vanish, and memories of witnesses may fade, jeopardizing your opportunity for a positive resolution for your case. I urge you to contact me today where my compassion and expertise can help develop a strategy to win your case and provide you clarity and calm.
          </p>
          <a 
            href="tel:425-675-1224"
            data-testid="timely-action-cta"
            className="inline-flex items-center gap-3 bg-white text-[#4A9B9B] px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all duration-300 shadow-xl"
          >
            <Phone className="w-6 h-6" />
            Call me today at (425) 675-1224
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
